import { useState, useEffect, useMemo, useCallback } from 'react';
import { reportApi } from '../../api/report.api';
import { companyApi } from '../../api/company.api';
import SearchCombobox from './SearchCombobox';
import { toPersian } from '../../utils/persianNumber';
import { useCompanyDisplay } from '../../utils/companyDisplay';

const MODE_PRODUCT = 'product';
const MODE_COMPANY = 'company';

const emptySelection = () => ({
  product_code: '',
  product_name: '',
  amin_customer_code: '',
  amin_customer_name: '',
  amount: '',
});

/**
 * Finance invoice Amin picker UX:
 * 1. Read-only کد کالا + نام کالا
 * 2. Combobox below — search by product name OR company
 */
export default function AminInvoicePicker({ value, onChange, bridgeConfigured = true }) {
  const companyLabel = useCompanyDisplay();
  const [mode, setMode] = useState(MODE_PRODUCT);
  const [products, setProducts] = useState([]);
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [aminCustomers, setAminCustomers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companyQuery, setCompanyQuery] = useState('');

  const selection = value || emptySelection();

  const loadProducts = useCallback(async () => {
    if (!bridgeConfigured) return;
    setLoadingProducts(true);
    try {
      const res = await reportApi.getAminProducts({});
      setProducts(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setProducts([]);
    }
    setLoadingProducts(false);
  }, [bridgeConfigured]);

  const loadCrmCompanies = useCallback(async () => {
    try {
      const res = await companyApi.getCompanyNameList();
      setCrmCompanies(Array.isArray(res.data) ? res.data.filter(Boolean) : []);
    } catch {
      setCrmCompanies([]);
    }
  }, []);

  const searchAminCustomers = useCallback(async (q) => {
    if (!bridgeConfigured) return;
    setLoadingCompanies(true);
    try {
      const res = await reportApi.getAminCustomers(q ? { q, limit: 100 } : { limit: 100 });
      setAminCustomers(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setAminCustomers([]);
    }
    setLoadingCompanies(false);
  }, [bridgeConfigured]);

  useEffect(() => {
    if (bridgeConfigured) loadProducts();
    loadCrmCompanies();
  }, [bridgeConfigured, loadProducts, loadCrmCompanies]);

  useEffect(() => {
    if (mode === MODE_COMPANY && bridgeConfigured) {
      const t = setTimeout(() => searchAminCustomers(companyQuery), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [mode, companyQuery, bridgeConfigured, searchAminCustomers]);

  const productOptions = useMemo(
    () => products.map((p) => ({
      value: String(p.code ?? ''),
      label: p.name || `کد ${p.code}`,
      meta: p.code ? `کد: ${p.code}${p.price != null ? ` — ${toPersian(Number(p.price).toLocaleString('fa-IR'))} ریال` : ''}` : '',
      raw: p,
    })).filter((o) => o.value),
    [products],
  );

  const companyOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const q = companyQuery.trim().toLowerCase();

    crmCompanies.forEach((name) => {
      if (q && !`${name} ${companyLabel(name)}`.toLowerCase().includes(q)) return;
      const key = `crm:${name}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        value: key,
        label: companyLabel(name),
        meta: 'شرکت CRM',
        raw: { source: 'crm', name },
      });
    });

    aminCustomers.forEach((c) => {
      const code = c.code != null ? String(c.code) : '';
      const name = c.name || '';
      const label = name || code;
      if (q && !`${label} ${code}`.toLowerCase().includes(q)) return;
      const key = `amin:${code}`;
      if (!code || seen.has(key)) return;
      seen.add(key);
      out.push({
        value: key,
        label: name || `حساب ${code}`,
        meta: code ? `کد حساب امین: ${code}` : 'امین',
        raw: { source: 'amin', code, name },
      });
    });

    return out.slice(0, 100);
  }, [crmCompanies, aminCustomers, companyQuery, companyLabel]);

  const emit = (patch) => onChange?.({ ...selection, ...patch });

  const onProductPick = (opt) => {
    const p = opt?.raw;
    if (!p) return;
    emit({
      product_code: String(p.code ?? ''),
      product_name: p.name || '',
      amount: p.price != null && p.price !== '' ? String(p.price) : selection.amount,
    });
  };

  const onCompanyPick = (opt) => {
    const raw = opt?.raw;
    if (!raw) return;
    if (raw.source === 'amin') {
      emit({
        amin_customer_code: String(raw.code ?? ''),
        amin_customer_name: raw.name || '',
      });
    } else {
      emit({
        amin_customer_code: '',
        amin_customer_name: raw.name || '',
      });
    }
  };

  if (!bridgeConfigured) {
    return (
      <div style={{
        background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
        padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#92400e', lineHeight: 1.7,
      }}
      >
        سرور حسابداری امین هنوز توسط دیسپچر پیکربندی نشده — کد کالا از امین در دسترس نیست.
      </div>
    );
  }

  const modeBtn = (id, text) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      style={{
        flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 12, fontWeight: 600,
        border: `1.5px solid ${mode === id ? '#29AAE1' : '#e2e8f0'}`,
        background: mode === id ? '#EDF3FC' : '#fff',
        color: mode === id ? '#233A7A' : '#64748b',
      }}
    >
      {text}
    </button>
  );

  return (
    <div style={{
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
      padding: '14px 14px 4px', marginBottom: 12,
    }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
        کالا / حسابداری امین
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>کد کالا</label>
          <input
            readOnly
            value={selection.product_code || '—'}
            style={{
              width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6,
              fontSize: 12, background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit', color: '#0f172a',
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>نام کالا</label>
          <input
            readOnly
            value={selection.product_name || '—'}
            style={{
              width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6,
              fontSize: 12, background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit', color: '#0f172a',
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {modeBtn(MODE_PRODUCT, 'نام کالا')}
        {modeBtn(MODE_COMPANY, 'شرکت')}
      </div>

      {mode === MODE_PRODUCT ? (
        <SearchCombobox
          label="جستجو و انتخاب کالا / خدمت"
          placeholder="نام یا کد کالا را بنویسید..."
          options={productOptions}
          value={selection.product_code}
          onChange={() => {}}
          onSelect={onProductPick}
          loading={loadingProducts}
          emptyText="کالایی در امین یافت نشد"
        />
      ) : (
        <SearchCombobox
          label="جستجو و انتخاب شرکت"
          placeholder="نام شرکت CRM یا طرف‌حساب امین..."
          options={companyOptions}
          value={selection.amin_customer_code ? `amin:${selection.amin_customer_code}` : (selection.amin_customer_name ? `crm:${selection.amin_customer_name}` : '')}
          onChange={(v) => {
            if (!v) {
              emit({ amin_customer_code: '', amin_customer_name: '' });
            }
          }}
          onSearchChange={setCompanyQuery}
          onSelect={(opt) => {
            setCompanyQuery(opt.label);
            onCompanyPick(opt);
          }}
          loading={loadingCompanies}
          emptyText="شرکتی یافت نشد"
        />
      )}

      {selection.amin_customer_name && mode === MODE_COMPANY && (
        <p style={{ fontSize: 11, color: '#16a34a', margin: '0 0 10px' }}>
          طرف‌حساب: {selection.amin_customer_name}
          {selection.amin_customer_code ? ` (کد ${selection.amin_customer_code})` : ''}
        </p>
      )}
    </div>
  );
}
